import React, { useState, useEffect, useRef } from 'react';
import './SimpleTimePicker.css';

const SimpleTimePicker = ({ 
  initialTime, 
  onTimeChange, 
  onClose,
  onConfirm 
}) => {
  // Use current time as default if no initialTime provided
  const defaultTime = initialTime || new Date();
  
  const [selectedHour, setSelectedHour] = useState(defaultTime.getHours());
  const [selectedMinute, setSelectedMinute] = useState(defaultTime.getMinutes());
  
  const hourRef = useRef(null);
  const minuteRef = useRef(null);

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  const ITEM_HEIGHT = 40;
  const VISIBLE_ITEMS = 5; // Number of visible items in the wheel

  // Initialize when component mounts - only set initial scroll position
  useEffect(() => {
    const currentHour = defaultTime.getHours();
    const currentMinute = defaultTime.getMinutes();
    
    // Set initial scroll position only once
    const scrollToTime = () => {
      if (hourRef.current) {
        // Start from the middle set of hours (24-47) to allow scrolling in both directions
        const middleSetStart = 24; // Start of middle set
        const scrollPosition = (middleSetStart + currentHour) * ITEM_HEIGHT;
        hourRef.current.scrollTop = scrollPosition;
      }
      if (minuteRef.current) {
        // Start from the middle set of minutes (60-119) to allow scrolling in both directions
        const middleSetStart = 60; // Start of middle set
        const scrollPosition = (middleSetStart + currentMinute) * ITEM_HEIGHT;
        minuteRef.current.scrollTop = scrollPosition;
      }
    };
    
    // Only scroll once on mount
    setTimeout(scrollToTime, 100);
  }, []); // Empty dependency array - only run once

  const handleHourScroll = (e) => {
    const scrollTop = e.target.scrollTop;
    const index = Math.round(scrollTop / ITEM_HEIGHT);
    
    // Calculate the actual hour value from the circular array
    // Since we have 3 sets of 24 hours (0-23, 24-47, 48-71), we need to map back to 0-23
    let newHour = index % 24;
    if (newHour < 0) newHour += 24;
    
    if (newHour !== selectedHour) {
      setSelectedHour(newHour);
      
      if (onTimeChange) {
        const newDate = new Date(defaultTime);
        newDate.setHours(newHour, selectedMinute);
        onTimeChange(newDate);
      }
    }
  };

  const handleMinuteScroll = (e) => {
    const scrollTop = e.target.scrollTop;
    const index = Math.round(scrollTop / ITEM_HEIGHT);
    
    // Calculate the actual minute value from the circular array
    // Since we have 3 sets of 60 minutes (0-59, 60-119, 120-179), we need to map back to 0-59
    let newMinute = index % 60;
    if (newMinute < 0) newMinute += 60;
    
    if (newMinute !== selectedMinute) {
      setSelectedMinute(newMinute);
      
      if (onTimeChange) {
        const newDate = new Date(defaultTime);
        newDate.setHours(selectedHour, newMinute);
        onTimeChange(newDate);
      }
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      const newDate = new Date(defaultTime);
      newDate.setHours(selectedHour, selectedMinute);
      onConfirm(newDate);
    }
    if (onClose) {
      onClose();
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  // Create extended arrays for circular scrolling
  const createCircularArray = (array, repeatCount = 3) => {
    const result = [];
    for (let i = 0; i < repeatCount; i++) {
      result.push(...array);
    }
    return result;
  };

  const circularHours = createCircularArray(hours);
  const circularMinutes = createCircularArray(minutes);

  return (
    <div className="simple-time-picker-overlay">
      <div className="simple-time-picker-modal">
        <div className="simple-time-picker-content">
          <div className="simple-time-picker-wheels">
            {/* Hour wheel */}
            <div className="simple-time-picker-wheel-container">
              <div 
                className="simple-time-picker-wheel" 
                ref={hourRef} 
                onScroll={handleHourScroll}
              >
                {circularHours.map((hour, index) => (
                  <div 
                    key={`hour-${index}`} 
                    className={`simple-time-picker-item ${hour === selectedHour ? 'simple-time-picker-item-selected' : ''}`}
                  >
                    {hour.toString().padStart(2, '0')}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Separator */}
            <div className="simple-time-picker-separator">:</div>
            
            {/* Minute wheel */}
            <div className="simple-time-picker-wheel-container">
              <div 
                className="simple-time-picker-wheel" 
                ref={minuteRef} 
                onScroll={handleMinuteScroll}
              >
                {circularMinutes.map((minute, index) => (
                  <div 
                    key={`minute-${index}`} 
                    className={`simple-time-picker-item ${minute === selectedMinute ? 'simple-time-picker-item-selected' : ''}`}
                  >
                    {minute.toString().padStart(2, '0')}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="simple-time-picker-actions">
            <button className="simple-time-picker-btn simple-time-picker-btn-cancel" onClick={handleCancel}>
              CANCEL
            </button>
            <button className="simple-time-picker-btn simple-time-picker-btn-ok" onClick={handleConfirm}>
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleTimePicker; 